import { calculateStickyOffsets } from './stickyStack';

const stickyLayerClass = 'mhecomm-sticky-layer';
const documentPositionFollowing = 4;

export function createStickyStackManager({ documentRef = document, windowRef = window } = {}) {
  const layers = new Map();
  let resizeObserver;
  let mutationObserver;
  let updateScheduled = false;
  let managedTargets = new Set();
  let scrollPaddingApplied = false;
  let previousScrollPadding = '';

  const isVisible = (element) => {
    const styles = windowRef.getComputedStyle(element);
    return styles.display !== 'none' && styles.visibility !== 'hidden';
  };

  const isActiveTarget = (element) => isVisible(element) && windowRef.getComputedStyle(element).position === 'sticky';

  const sortLayers = (leftLayer, rightLayer) => {
    if (leftLayer.orderElement === rightLayer.orderElement) {
      return 0;
    }

    return leftLayer.orderElement.compareDocumentPosition(rightLayer.orderElement) & documentPositionFollowing ? -1 : 1;
  };

  const getActiveLayers = () => {
    return Array.from(layers.values())
      .map((layer) => ({
        ...layer,
        targets: layer.targets.filter(isActiveTarget),
      }))
      .filter((layer) => layer.targets.length > 0)
      .sort(sortLayers);
  };

  const scheduleUpdate = () => {
    if (updateScheduled) {
      return;
    }

    updateScheduled = true;
    const update = () => {
      updateScheduled = false;
      refresh();
    };

    if (windowRef.requestAnimationFrame) {
      windowRef.requestAnimationFrame(update);
    } else {
      windowRef.setTimeout(update, 0);
    }
  };

  const syncObservers = () => {
    const targets = Array.from(layers.values()).flatMap((layer) => layer.targets);
    const uniqueTargets = new Set(targets);

    resizeObserver?.disconnect();
    uniqueTargets.forEach((target) => resizeObserver?.observe(target));

    mutationObserver?.disconnect();
    uniqueTargets.forEach((target) => {
      mutationObserver?.observe(target, { attributes: true, attributeFilter: ['class'] });
    });
  };

  const restoreScrollPadding = () => {
    if (!scrollPaddingApplied) {
      return;
    }

    if (previousScrollPadding) {
      documentRef.documentElement.style.setProperty('scroll-padding-top', previousScrollPadding);
    } else {
      documentRef.documentElement.style.removeProperty('scroll-padding-top');
    }
    scrollPaddingApplied = false;
  };

  function refresh() {
    const activeLayers = getActiveLayers();
    const layerHeights = activeLayers.map((layer) => Math.max(...layer.targets.map((target) => target.offsetHeight)));
    const { offsets, totalHeight } = calculateStickyOffsets(layerHeights);
    const nextManagedTargets = new Set();

    activeLayers.forEach((layer, index) => {
      layer.targets.forEach((target) => {
        target.classList.add(stickyLayerClass);
        target.style.setProperty('--mhecomm-sticky-top', `${offsets[index]}px`);
        nextManagedTargets.add(target);
      });
    });

    managedTargets.forEach((target) => {
      if (!nextManagedTargets.has(target)) {
        target.classList.remove(stickyLayerClass);
        target.style.removeProperty('--mhecomm-sticky-top');
      }
    });

    managedTargets = nextManagedTargets;

    if (totalHeight > 0) {
      if (!scrollPaddingApplied) {
        previousScrollPadding = documentRef.documentElement.style.getPropertyValue('scroll-padding-top');
        scrollPaddingApplied = true;
      }
      documentRef.documentElement.style.setProperty('scroll-padding-top', `${totalHeight}px`);
    } else {
      restoreScrollPadding();
    }

    syncObservers();
  }

  const initializeObservers = () => {
    if (!resizeObserver) {
      const ResizeObserverConstructor = windowRef.ResizeObserver;
      if (ResizeObserverConstructor) {
        resizeObserver = new ResizeObserverConstructor(() => scheduleUpdate());
      }
    }

    if (!mutationObserver) {
      const MutationObserverConstructor = windowRef.MutationObserver;
      if (MutationObserverConstructor) {
        mutationObserver = new MutationObserverConstructor(() => scheduleUpdate());
      }
      windowRef.addEventListener('resize', scheduleUpdate);
    }
  };

  const registerLayer = (id, targets, orderElement) => {
    const uniqueTargets = Array.from(new Set(targets.filter(Boolean)));
    if (!uniqueTargets.length) {
      layers.delete(id);
      scheduleUpdate();
      return () => undefined;
    }

    layers.set(id, { id, targets: uniqueTargets, orderElement: orderElement || uniqueTargets[0] });
    initializeObservers();
    scheduleUpdate();

    return () => {
      layers.delete(id);
      scheduleUpdate();
    };
  };

  const destroy = () => {
    resizeObserver?.disconnect();
    mutationObserver?.disconnect();
    windowRef.removeEventListener('resize', scheduleUpdate);
    managedTargets.forEach((target) => {
      target.classList.remove(stickyLayerClass);
      target.style.removeProperty('--mhecomm-sticky-top');
    });
    managedTargets = new Set();
    layers.clear();
    restoreScrollPadding();
  };

  return { destroy, refresh, registerLayer };
}

export const stickyStackManager = createStickyStackManager();
